/**
 * P13-V7B — what a navigation surface may OFFER to the current session.
 *
 * One question, asked of the router itself: "is there a screen at this target, and does its route
 * admit the role of this session?". A menu that asks it cannot advertise a path that does not exist, a
 * record that only redirects, or a page the guard would refuse — because the answer is computed from
 * the very route table and the very role rule (`isRoleAdmittedByRouteMeta`) the guard uses.
 *
 * It is a VISIBILITY rule and grants nothing. The guard still runs on every navigation and the backend
 * remains the only authority over data.
 */
import { useRouter, type RouteLocationRaw, type Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { isRoleAdmittedByRouteMeta } from '@/router/guards'

/** Pure core, usable without a component instance (tests, stores). */
export function routeAdmits(router: Router, target: RouteLocationRaw, role: string | null | undefined): boolean {
  let resolved
  try {
    resolved = router.resolve(target)
  } catch {
    return false // an unknown route NAME throws: there is nothing to offer
  }
  if (!resolved.matched.length) return false

  // The leaf must render a screen. A record that only redirects (legacy aliases, contained lab
  // surfaces) is not a destination a menu may promise.
  const leaf = resolved.matched[resolved.matched.length - 1]
  const rendersScreen = Boolean(leaf.components && Object.keys(leaf.components).length > 0)
  if (!rendersScreen) return false

  return isRoleAdmittedByRouteMeta(resolved.meta as Record<string, any>, role)
}

export function useRouteAdmission() {
  const router = useRouter()
  const authStore = useAuthStore()

  /** True when `target` is a screen the current session's role may open. */
  const admits = (target: RouteLocationRaw): boolean => routeAdmits(router, target, authStore.userRole)

  /** Convenience for the common menu shape `:to="{ name }"`. */
  const admitsName = (name: string): boolean => admits({ name })

  return { admits, admitsName }
}
